import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
    fleetCreateToRestV1,
    fleetDetailFromRestV1,
    fleetFromRestV1,
    fleetsFromRestV1,
    fleetUpdateToRestV1,
} from 'domain/mappers/fleetFromRestV1';
import type { CreateFleetInputFE, FleetDetailFE, FleetFE, UpdateFleetInputFE } from 'domain/systemAdmin';

import { errorHandler, formatAxiosError, isAxiosError } from '../errors';
import { correlationHeaders, managementRestApi } from '../restClient';

const listSystemFleetsQueryFn = async (): Promise<{ data: FleetFE[] }> => {
    try {
        const response = await managementRestApi.get<unknown>('/api/v1/fleets', correlationHeaders());
        return { data: fleetsFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[listFleets]: Unexpected error occurred while fetching system fleets', loggableError);
    }
};

const getSystemFleetAttributeQueryFn = async (id: string): Promise<{ data: FleetDetailFE }> => {
    try {
        const response = await managementRestApi.get<unknown>(`/api/v1/fleets/${id}`, correlationHeaders());
        return { data: fleetDetailFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            `[getFleetById]: Unexpected error occurred while fetching system fleet ${id} attributes`,
            loggableError
        );
    }
};

const createFleetFn = async (input: CreateFleetInputFE): Promise<{ data: FleetFE }> => {
    try {
        const response = await managementRestApi.post<unknown>(
            '/api/v1/fleets',
            fleetCreateToRestV1(input),
            correlationHeaders()
        );
        return { data: fleetFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[createFleet]: Unexpected error occurred while creating the fleet', loggableError);
    }
};

const updateFleetFn = async (input: UpdateFleetInputFE): Promise<{ data: FleetFE }> => {
    try {
        const response = await managementRestApi.put<unknown>(
            `/api/v1/fleets/${input.id}`,
            fleetUpdateToRestV1(input),
            correlationHeaders()
        );
        return { data: fleetFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            `[updateFleet]: Unexpected error occurred while updating the fleet with id: ${input.id}`,
            loggableError
        );
    }
};

export const systemFleetsApi = createApi({
    reducerPath: 'systemFleets',
    tagTypes: ['Fleets'],
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        // The `string` arg is the cache key, not an input; it reaches no request.
        listSystemFleets: builder.query<FleetFE[], string>({
            queryFn: listSystemFleetsQueryFn,
            providesTags: ['Fleets'],
        }),
        getSystemFleetsAttributes: builder.query<FleetDetailFE, string>({
            queryFn: getSystemFleetAttributeQueryFn,
        }),
        updateSystemFleet: builder.mutation<FleetFE, UpdateFleetInputFE>({
            queryFn: updateFleetFn,
            invalidatesTags: ['Fleets'],
        }),
        createSystemFleet: builder.mutation<FleetFE, CreateFleetInputFE>({
            queryFn: createFleetFn,
            invalidatesTags: ['Fleets'],
        }),
    }),
});

export const {
    useListSystemFleetsQuery,
    useGetSystemFleetsAttributesQuery,
    useUpdateSystemFleetMutation,
    useCreateSystemFleetMutation,
} = systemFleetsApi;
